import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Select from 'react-select';
import {
  updateMealComponent,
  deleteMealComponent,
  getCountries,
  addMealComponent,
} from "../../api/api_meals";
import SustainabilityValue from "../SustainabilityValue";
import IngredientParametersModal from "../IngredientParametersModal";
import ProductCompareModal from "../ProductCompareModal";
import { getCountries as getCountriesFromDishes } from "../../api/api_dishes";
import { getComponentNovaImage } from "../../utils/novaUtils";
import DishEnergyDistributionChart from "../DishEnergyDistributionChart";
import { useTranslation } from "../../hooks/useTranslation";
import SideDrawer from "../navigation/SideDrawer";
import { CiViewList } from "react-icons/ci";
import {
  formatComponentWeightInAmount,
  getComponentWeightInAmount,
} from "../../utils/componentWeightDisplay";

const MealTableRowComponent = (props) => {
  let component = props.component;
  const mealId = props.mealId; // mealId is required for meal component API calls
  const { t, currentLanguage } = useTranslation();

  const isProductConnection =
    component.source_type === "product" ||
    component.sourceType === "product" ||
    component.type === "product" ||
    !!component.product_id;

  const productId =
    component.product_id ||
    component.source_id ||
    component.product?.id ||
    null;

  const productDerivedIsEco = !!(
    component.is_eco ??
    component.product?.is_eco ??
    component.product?.is_organic
  );

  const [editing, setEditing] = useState(false);
  const [editAmount, setEditAmount] = useState(
    () => getComponentWeightInAmount(component) ?? component.amount
  );
  const [editPurchasePricePerKg, setEditPurchasePricePerKg] = useState(component.purchase_price_per_kg ?? "");
  const [editCountry, setEditCountry] = useState(null);
  const [countryChanged, setCountryChanged] = useState(false);
  const [editIsEco, setEditIsEco] = useState(productDerivedIsEco);
  const [editComment, setEditComment] = useState(component.comment || '');
  const [editHeading, setEditHeading] = useState(component.heading || '');
  const [loading, setLoading] = useState(false);
  const [showParametersModal, setShowParametersModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const compareSourceProduct = useMemo(() => {
    if (!isProductConnection || !productId) return null;
    return {
      id: productId,
      name: component.name || component.product_name || component.product?.name,
      gtin: component.gtin || component.product?.gtin,
      brand: component.brand || component.product?.brand?.name || component.product?.brand,
      image_url: component.image_url || component.product?.image_url || component.product?.default_image_url,
      nova: component.nova ?? component.product?.nova,
      is_eco: component.is_eco ?? component.product?.is_eco,
      co2e_equivalent: component.co2e_equivalent,
      climate_score: component.climate_score,
      water_score: component.water_score,
      health_score: component.health_score,
      biodiversity_score: component.biodiversity_score,
      land_use_score: component.land_use_score,
      transport_score: component.transport_score,
      fertilizers_use_score: component.fertilizers_use_score,
      pesticides_use_score: component.pesticides_use_score,
      antibiotics_resistance_score: component.antibiotics_resistance_score,
      social_justice_score: component.social_justice_score,
      child_labor_score: component.child_labor_score,
      equality_score: component.equality_score,
      worker_rights_score: component.worker_rights_score,
      local_population_score: component.local_population_score,
      animal_protection_score: component.animal_protection_score,
      origin_country_id: component.origin_country_id,
      size: component.product?.size ?? component.size,
      default_unit_id: component.product?.unit_id ?? component.default_unit_id ?? component.unit_id,
      default_unit_abbr: component.unit_abbr || component.product?.unit_abbr || component.chosen_unit || component.unit,
      calculated_average_price: component.product?.calculated_average_price,
      calculated_average_price_unit_id: component.product?.calculated_average_price_unit_id,
    };
  }, [isProductConnection, productId, component]);
  const [showCommentPreview, setShowCommentPreview] = useState(false);
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [gtinCopied, setGtinCopied] = useState(false);
  const inputAmount = useRef(null);

  const handleCopyGtin = (e, gtin) => {
    e.preventDefault();
    e.stopPropagation();
    if (!gtin) return;
    navigator.clipboard.writeText(gtin).then(() => {
      setGtinCopied(true);
      setTimeout(() => setGtinCopied(false), 2000);
    }).catch(() => {});
  };

  /* Load countries on component mount */
  useEffect(() => {
    const loadCountries = async () => {
      try {
        // Try meal API first, fallback to dishes API if needed
        let response;
        try {
          response = await getCountries();
        } catch (e) {
          // Fallback to dishes API
          response = await getCountriesFromDishes();
        }
        
        if (response.data) {
          setCountryOptions(response.data);
          
          // Update editCountry with the correct country object if we have a country ID
          if (component.origin_country_id && !editCountry) {
            const matchingCountry = response.data.find(country => country.value === component.origin_country_id);
            if (matchingCountry) {
              setEditCountry(matchingCountry);
            }
          } else if (component.origin && !editCountry) {
            // If we don't have origin_country_id but we have origin (country name), try to find by name
            const matchingCountry = response.data.find(country => country.label === component.origin);
            if (matchingCountry) {
              setEditCountry(matchingCountry);
            }
          }
        }
      } catch (error) {
        console.error('Error loading countries:', error);
      }
    };
    loadCountries();
  }, [component.origin_country_id, component.origin, editCountry]);

  // Update editCountry when component data changes
  useEffect(() => {
    if (countryOptions.length > 0 && !editCountry) {
      if (component.origin_country_id) {
        const matchingCountry = countryOptions.find(country => country.value === component.origin_country_id);
        if (matchingCountry) {
          setEditCountry(matchingCountry);
        }
      } else if (component.origin) {
        const matchingCountry = countryOptions.find(country => country.label === component.origin);
        if (matchingCountry) {
          setEditCountry(matchingCountry);
        }
      }
    }
  }, [countryOptions, component.origin_country_id, component.origin, editCountry]);

  /* UI functions */
  const hideOnEdit = (c) => (editing ? "hidden" : c);
  const showOnEdit = (c) => (editing ? c : "hidden");

  /* API calls using API client */
  const handleRemoveComponentConnection = async () => {
    if (!mealId || !component.id) {
      console.error("Meal ID or component ID missing");
      return;
    }
    
    setLoading(true);
    try {
      await deleteMealComponent(mealId, component.id);
      // Refresh the meal data using Redux
      props.updateItem();
    } catch (error) {
      console.error("Error removing component:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditComponentConnection = async () => {
    if (!mealId || !component.id) {
      console.error("Meal ID or component ID missing");
      return;
    }
    
    setLoading(true);
    try {
      const editData = {
        weight_in_amount: editAmount,
        purchase_price_per_kg: editPurchasePricePerKg === "" ? null : parseFloat(editPurchasePricePerKg),
        is_eco: isProductConnection ? productDerivedIsEco : editIsEco,
        comment: editComment,
        heading: editHeading
      };

      // Only include origin_country_id if the user actually changed the country
      if (countryChanged) {
        editData.origin_country_id = editCountry?.value || null;
      }

      await updateMealComponent(mealId, component.id, editData);
      // Refresh the meal data using Redux
      props.updateItem();
      setCountryChanged(false);
      setEditing(false);
    } catch (error) {
      console.error("Error editing component:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset all edit values to original component values
    setEditAmount(component.amount);
    setEditPurchasePricePerKg(component.purchase_price_per_kg ?? "");
    
    // Find the correct country object from the loaded options
    if (component.origin_country_id) {
      const matchingCountry = countryOptions.find(country => country.value === component.origin_country_id);
      setEditCountry(matchingCountry || null);
    } else if (component.origin) {
      const matchingCountry = countryOptions.find(country => country.label === component.origin);
      setEditCountry(matchingCountry || null);
    } else {
      setEditCountry(null);
    }
    
    setEditIsEco(productDerivedIsEco);
    setEditComment(component.comment || '');
    setEditHeading(component.heading || '');
    setCountryChanged(false);
    setEditing(false);
  };

  const nutritionDetails = (() => {
    const pools = [
      component?.nutrients,
      component?.product?.nutrients,
      component?.ingredient?.nutrients,
    ];
    const nutrientsArray = pools.find((p) => Array.isArray(p) && p.length > 0) || [];

    const pickFromArray = (names) => {
      if (!nutrientsArray.length) return null;
      const wanted = names.map((n) => n.toLowerCase());
      const found = nutrientsArray.find((n) =>
        wanted.includes(String(n?.name || "").trim().toLowerCase())
      );
      if (!found) return null;
      const raw = found?.amount_per_100g ?? found?.amount ?? found?.value;
      const value = Number(raw);
      return {
        value: Number.isFinite(value) ? value : null,
        unit: found?.unit_abbr || found?.unit || "",
      };
    };

    const pickFromFlat = (keys, fallbackUnit = "") => {
      for (const key of keys) {
        const value = Number(component?.[key] ?? component?.product?.[key] ?? component?.ingredient?.[key]);
        if (Number.isFinite(value)) {
          return { value, unit: fallbackUnit };
        }
      }
      return null;
    };

    return {
      energy:
        pickFromArray(["energi", "energy", "kilokalorier", "kcal"]) ||
        pickFromFlat(["kcal", "energy_kcal", "energy"], "kcal"),
      protein: pickFromArray(["protein", "proteiner"]) || pickFromFlat(["protein"], "g"),
      carbs:
        pickFromArray(["kolhydrater", "carbohydrates", "carbs"]) ||
        pickFromFlat(["carbs", "carbohydrates"], "g"),
      fiber: pickFromArray(["fibrer", "fiber", "kostfiber"]) || pickFromFlat(["fiber", "fibrer"], "g"),
      fat: pickFromArray(["fett", "fat"]) || pickFromFlat(["fat", "fett"], "g"),
    };
  })();

  const hasNutritionData = Object.values(nutritionDetails).some((entry) => Number.isFinite(entry?.value));
  const componentAmount = Number(component?.amount ?? component?.weight_in_amount ?? 0);
  const nutrientRows = (() => {
    const priority = [
      "energi",
      "protein",
      "fibrer",
      "fett",
      "mättat fett",
      "sockerarter",
      "kolhydrater",
      "salt",
      "kalium",
      "natrium",
      "fosfor",
    ];

    if (Array.isArray(component?.nutrients) && component.nutrients.length > 0) {
      const filtered = component.nutrients.filter((n) => {
        const nm = (n?.name || "").toString().trim().toLowerCase();
        const amount = Number(n?.amount_per_100g ?? n?.amount ?? 0);
        return priority.includes(nm) && Number.isFinite(amount);
      });
      return filtered.sort((a, b) => {
        const an = (a?.name || "").toString().trim().toLowerCase();
        const bn = (b?.name || "").toString().trim().toLowerCase();
        return priority.indexOf(an) - priority.indexOf(bn);
      });
    }

    const fallback = [
      { name: "Energi", ...nutritionDetails.energy },
      { name: "Protein", ...nutritionDetails.protein },
      { name: "Kolhydrater", ...nutritionDetails.carbs },
      { name: "Fibrer", ...nutritionDetails.fiber },
      { name: "Fett", ...nutritionDetails.fat },
    ];
    return fallback
      .filter((n) => Number.isFinite(n?.value))
      .map((n) => ({
        name: n.name,
        amount_per_100g: n.value,
        unit_abbr: n.unit || "",
      }));
  })();
  const displayName =
    currentLanguage !== 'sv' && component?.name_eng
      ? component.name_eng
      : component?.name;

  return (
    <>
      <tr
        key={component.id}
        className="hover:bg-gray-50 transition-colors"
        draggable={props.draggable}
        onDragStart={(e) => props.onDragStart && props.onDragStart(e, component)}
        onDragOver={(e) => props.onDragOver && props.onDragOver(e, component)}
        onDrop={(e) => props.onDrop && props.onDrop(e, component)}
        data-id={component.id}
      >
        <td className="px-6 py-4 font-medium text-gray-900">
          <div className="flex flex-col">
            <span className="inline-flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-gray-800 cursor-move"
                fill="none"
                stroke={component.heading && component.heading.trim() ? 'green' : 'currentColor'}
                viewBox="0 0 24 24"
                aria-label={t("Dra för att flytta")}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h.01M12 6h.01M16 6h.01M8 12h.01M12 12h.01M16 12h.01M8 18h.01M12 18h.01M16 18h.01" />
              </svg>
              {displayName || t("Okänd komponent")}
            </span>
            {(component.source_type === 'product' || component.sourceType === 'product') && (component.gtin || component.product?.gtin) && (
              <span className="inline-flex items-center gap-1.5 mt-0.5 ml-6 text-xs text-gray-500">
                <span className="font-mono select-text" title={t("GTIN – välj och kopiera eller klicka för att kopiera")}>
                  {component.gtin || component.product?.gtin}
                </span>
                <button
                  type="button"
                  onClick={(e) => handleCopyGtin(e, component.gtin || component.product?.gtin)}
                  className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  title={t("Kopiera GTIN")}
                  aria-label={t("Kopiera GTIN")}
                >
                  {gtinCopied ? (
                    <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </span>
            )}
            {(component.source_type === 'ingredient' || component.sourceType === 'ingredient') && (
              <span className="text-xs text-gray-500 mt-0.5 ml-6">{t("Ingrediens")}</span>
            )}
            {(component.source_type === 'recipe' || component.sourceType === 'recipe') && component.source_id && (
              <span className="inline-flex items-center mt-0.5 ml-6">
                <small className="text-xs text-gray-500">{t("Måltid")} </small>
                <a
                  href={`/items/${component.source_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 inline-flex"
                  title={t("Öppna recept (ny flik)")}
                  aria-label={t("Öppna recept i ny flik")}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                </a>
              </span>
            )}
            <div className="mt-0.5 ml-6 flex flex-col items-start gap-1.5">
              <img
                src={getComponentNovaImage(component)}
                alt={t("NOVA")}
                className="w-16 h-16 block"
                title={`NOVA ${component.nova ?? component.product?.nova ?? component.novaflag ?? ''} (1–4)`}
              />
              {isProductConnection && productId && mealId && (
                <button
                  type="button"
                  onClick={() => setShowCompareModal(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                >
                  <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-95" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  {t('Jämföre')}
                </button>
              )}
            </div>
            {(() => {
              const full = String(component.comment || "").trim();
              if (!full) return null;
              const short =
                full.length > 20 ? `${full.slice(0, 20)}...` : full;
              return (
                <p
                  className="mt-1 ml-6 text-xs text-gray-600 leading-snug m-0 max-w-[min(100%,18rem)]"
                  title={full.length > 20 ? full : undefined}
                >
                  {short}
                </p>
              );
            })()}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-col space-y-2">
            <SustainabilityValue component={component} />
            {/* Duplicate CO2e/totalt display */}
            {(() => {
              const co2Value = parseFloat(component.co2e_equivalent);
              const size = parseFloat(component.amount);
              
              // Calculate: (CO2e/kg / 1000) * size
              const CO2e_totalt = (Number.isFinite(co2Value) && Number.isFinite(size))
                ? parseFloat(((co2Value / 1000) * size).toFixed(3))
                : null;
              const grayScaleClass = CO2e_totalt === null || CO2e_totalt === 0 ? "grayscale opacity-50" : "";
              
              return (
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <img
                      className={`w-5 h-5 ${grayScaleClass}`}
                      src={`${process.env.PUBLIC_URL}/assets/icons/Klimat.png`}
                      alt={t("CO2e")}
                    />
                    <span className="text-sm">{t("CO2e-bidrag (kg)")}</span>
                  </div>
                  <div className="text-gray-600 font-normal text-sm">
                    {CO2e_totalt !== null && CO2e_totalt !== undefined ? CO2e_totalt : "-"}
                  </div>
                </div>
              );
            })()}
          </div>
        </td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-gray-700 ${hideOnEdit(
            ""
          )}`}
        >
          {formatComponentWeightInAmount(component)}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap ${showOnEdit("")}`}>
          <input
            className="input-field"
            type="number"
            ref={inputAmount}
            value={editAmount}
            onChange={(e) => {
              setEditAmount(e.target.value);
            }}
            placeholder={t("Mängd")}
            min="0"
            step="0.01"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
          {component.percentage}%
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
          {editing ? (
            <input
              className="input-field"
              type="number"
              value={editPurchasePricePerKg}
              onChange={(e) => setEditPurchasePricePerKg(e.target.value)}
              placeholder={t("Pris/kg")}
              min="0"
              step="0.01"
            />
          ) : (
            component.purchase_total ?? "-"
          )}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap text-gray-700 ${hideOnEdit("")}`}>
          {component.origin || t("Okänt")}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap ${showOnEdit("")}`}>
          <Select
            value={editCountry}
            onChange={(selectedOption) => {
              setEditCountry(selectedOption);
              setCountryChanged(true);
            }}
            options={countryOptions}
            placeholder={t("Ursprungsland...")}
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable
            isDisabled={component.type === 'product' || component.source_type === 'product'}
            noOptionsMessage={() => t('Inga länder hittades')}
            loadingMessage={() => t('Laddar länder...')}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2 relative">
            {editing ? (
              <>
                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-50"
                  onClick={() => handleEditComponentConnection()}
                  disabled={loading}
                  title={t("Spara")}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <img src="/assets/icons/Check.png" alt={t("Spara")} className="w-4 h-4" />
                  )}
                </button>
                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-500 hover:bg-gray-600 text-white transition-colors"
                  onClick={handleCancelEdit}
                  title={t("Avbryt")}
                >
                  <span className="text-xs font-bold">×</span>
                </button>
              </>
            ) : (
              <>
                {/* Comment Preview Button */}
                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                  onClick={() => setShowCommentPreview((v) => !v)}
                  title={showCommentPreview ? t("Dölj kommentar") : t("Visa kommentar")}
                >
                  <img
                    src="/assets/icons/Message.png"
                    alt={t("Kommentar")}
                    className="w-4 h-4"
                  />
                </button>
                {/* Nutrition Button */}
                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                  onClick={() => setShowNutritionModal(true)}
                  title={t("Visa näringsvärde")}
                >
                  <CiViewList className="w-4 h-4 text-gray-700" />
                </button>
                {/* Parameters Button */}
                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 hover:bg-secondary-green text-white transition-colors"
                  onClick={() => setShowParametersModal(true)}
                  title={t("Visa parametrar")}
                >
                  <img src="/assets/icons/svg/parameter.svg" alt={t("Parametrar")} className="w-4 h-4" />
                </button>
                
                {/* Edit Button */}
                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 hover:bg-secondary-green text-white transition-colors"
                  onClick={() => setEditing(true)}
                  title={t("Redigera")}
                >
                  <img src="/assets/icons/Redigera.png" alt={t("Redigera")} className="w-4 h-4" />
                </button>
              </>
            )}
            
            <button
              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
              onClick={() => handleRemoveComponentConnection()}
              disabled={loading}
              title={t("Ta bort")}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <img src="/assets/icons/Radera.png" alt={t("Ta bort")} className="w-4 h-4" />
              )}
            </button>

            {showCommentPreview && (
              <div className="absolute left-8 bottom-full mb-2 z-10 max-w-xs bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700 text-left">
                {component.comment && String(component.comment).trim().length > 0 ? (
                  <span className="leading-relaxed">{component.comment}</span>
                ) : (
                  <span className="italic text-gray-400">{t("Ingen kommentar")}</span>
                )}
              </div>
            )}
          </div>
        </td>
      </tr>

      {/* Edit Details Row */}
      {editing && (
        <tr className="bg-gray-50">
          <td colSpan="7" className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Eco Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Ekologisk")}
                </label>
                <label className={`flex items-center ${isProductConnection ? "cursor-not-allowed opacity-90" : ""}`}>
                  <input
                    type="checkbox"
                    checked={isProductConnection ? productDerivedIsEco : editIsEco}
                    onChange={(e) => setEditIsEco(e.target.checked)}
                    disabled={isProductConnection}
                    className="mr-2 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-600">{t("Ekologisk")}</span>
                </label>
              </div>

              {/* Heading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Rubrik")}
                </label>
                <input
                  type="text"
                  value={editHeading}
                  onChange={(e) => setEditHeading(e.target.value)}
                  placeholder={t("Rubrik för gruppering...")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Kommentar")}
                </label>
                <textarea
                  value={editComment}
                  onChange={(e) => setEditComment(e.target.value)}
                  placeholder={t("Kommentar...")}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                />
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* Ingredient Parameters Modal */}
      {showCompareModal && compareSourceProduct && (
        <ProductCompareModal
          isOpen={showCompareModal}
          onClose={() => setShowCompareModal(false)}
          sourceProduct={compareSourceProduct}
          onAddProduct={async (product) => {
            const weight =
              getComponentWeightInAmount(component) ??
              parseFloat(component.weight_in_amount ?? component.amount) ??
              0;
            if (!mealId || !product?.id || !(weight > 0)) {
              throw new Error(t('Kunde inte lägga till produkten'));
            }
            await addMealComponent(mealId, {
              source_type: 'product',
              source_id: product.id,
              weight_in_amount: weight,
              unit: component.chosen_unit || component.unit || 'g',
              origin_country_id:
                product.origin_country_id ||
                component.origin_country_id ||
                null,
              is_eco: !!(product.is_eco ?? component.is_eco),
              purchase_price_per_kg:
                product.purchase_price_per_kg === '' ||
                product.purchase_price_per_kg == null
                  ? null
                  : Number(product.purchase_price_per_kg),
              insert_after_id: component.id,
            });
            if (typeof props.updateItem === 'function') {
              props.updateItem();
            }
          }}
        />
      )}

      <IngredientParametersModal
        ingredient={component}
        isOpen={showParametersModal}
        onClose={() => setShowParametersModal(false)}
      />

      {/* Ingredient Nutrition Drawer (portal avoids invalid DOM nesting under <tbody>) */}
      {showNutritionModal && typeof document !== "undefined"
        ? createPortal(
            <SideDrawer
              open={showNutritionModal}
              onClose={() => setShowNutritionModal(false)}
              title={t("Näringsvärde ingrediensnivå")}
              titleHint={t("Energi, protein, fett, kolhydrater, fiber, sockerarter, salt, kalium, natrium, fosfor osv.")}
              titleIcon={
                <CiViewList className="w-6 h-6" />
              }
              ariaLabel={t("Näringsvärde ingrediensnivå")}
              zIndexClass="z-[1200]"
            >
              <p className="text-sm text-gray-600 mb-3 truncate">
                {displayName || t("Ingrediens")}
              </p>
              {hasNutritionData && nutrientRows.length > 0 ? (
                <div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            {t("Näringsämne")}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            {t("Per 100g")}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            {t("I rätten")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {nutrientRows.map((n, idx) => {
                          const amountPer100 = Number(n?.amount_per_100g ?? n?.amount ?? 0);
                          const inDish = Number.isFinite(amountPer100) && componentAmount > 0
                            ? (amountPer100 * componentAmount) / 100
                            : null;
                          return (
                            <tr key={`${n?.nutrient_id || n?.name || "n"}-${idx}`}>
                              <td className="px-4 py-2 text-sm text-gray-700">
                                {t(n?.name) || "-"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                {Number.isFinite(amountPer100) ? amountPer100.toFixed(2) : "-"} {n?.unit_abbr || n?.unit || ""}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                {inDish !== null ? inDish.toFixed(2) : "-"} {n?.unit_abbr || n?.unit || ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <DishEnergyDistributionChart nutrients={nutrientRows} t={t} />
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  {t("Ingen näringsinformation tillgänglig för denna ingrediens.")}
                </div>
              )}
            </SideDrawer>,
            document.body
          )
        : null}
    </>
  );
};

export default MealTableRowComponent;

